import type {
  OrderMessage,
  OrderMessageList,
  OrderTrack,
  RiderLocationEvent,
  TrackStage,
} from '@capsico/contracts';

/*
 * Pure merge rules for live order tracking (W14). The socket and the 15 s polling fallback both
 * write into one cached OrderTrack; these functions decide what wins, so a late or duplicated
 * event can never move the screen backwards. No React, no network — easy to reason about.
 */

/** Stages that still move; the socket and the map only run for these. */
export const MOVING_STAGES: readonly TrackStage[] = [
  'placed',
  'accepted',
  'preparing',
  'ready',
  'rider_assigned',
  'picked_up',
  'nearby',
];

/** The rider's position is shown only between pickup and delivery. */
export const RIDER_VISIBLE_STAGES: readonly TrackStage[] = ['picked_up', 'nearby'];

export function isMoving(track: OrderTrack | null | undefined): boolean {
  return !!track && MOVING_STAGES.includes(track.stage);
}

const time = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? 0 : value;
};

/**
 * A full OrderTrack arrived (socket `order.status` or a poll). The newer `updatedAt` wins; on a
 * tie the incoming copy wins (it is at least as fresh). A newer status without a rider point
 * keeps the last known point while the rider is still visible, so the marker never blinks out.
 */
export function applyStatus(previous: OrderTrack | undefined, next: OrderTrack): OrderTrack {
  if (!previous || previous.orderId !== next.orderId) return next;
  if (time(next.updatedAt) < time(previous.updatedAt)) return previous;
  if (
    next.riderLocation === null &&
    previous.riderLocation !== null &&
    RIDER_VISIBLE_STAGES.includes(next.stage)
  ) {
    return { ...next, riderLocation: previous.riderLocation };
  }
  if (
    next.riderLocation !== null &&
    previous.riderLocation !== null &&
    time(previous.riderLocation.at) > time(next.riderLocation.at)
  ) {
    return { ...next, riderLocation: previous.riderLocation };
  }
  return next;
}

/**
 * A rider point arrived (`order.rider_location`). Ignored for another order, before pickup,
 * after delivery, or when it is older than the point already shown. "Nearby" only ever turns on.
 */
export function applyRiderLocation(
  previous: OrderTrack | undefined,
  event: RiderLocationEvent,
): OrderTrack | undefined {
  if (!previous || previous.orderId !== event.orderId) return previous;
  if (!RIDER_VISIBLE_STAGES.includes(previous.stage)) return previous;
  if (previous.riderLocation && time(previous.riderLocation.at) >= time(event.at)) {
    return previous;
  }
  const nearby = previous.nearby || event.nearby;
  return {
    ...previous,
    riderLocation: { lat: event.lat, lng: event.lng, headingDeg: event.headingDeg, at: event.at },
    nearby,
    stage: nearby && previous.stage === 'picked_up' ? 'nearby' : previous.stage,
  };
}

/**
 * Adds a chat message (from the socket, our own send, or an optimistic draft) once, in time
 * order. The customer app is always the "customer" side, so `mine` follows the sender.
 */
export function mergeMessage(
  previous: OrderMessageList | undefined,
  message: OrderMessage,
): OrderMessageList | undefined {
  if (!previous) return previous;
  const mine = message.sender === 'customer';
  const rest = previous.messages.filter((entry) => entry.id !== message.id);
  const messages = [...rest, { ...message, mine }].sort((a, b) => time(a.at) - time(b.at));
  return { ...previous, messages };
}

/** Swaps an optimistic message for the saved one (or drops it when `saved` is null). */
export function replaceMessage(
  previous: OrderMessageList | undefined,
  tempId: string,
  saved: OrderMessage | null,
): OrderMessageList | undefined {
  if (!previous) return previous;
  const without = previous.messages.filter((entry) => entry.id !== tempId);
  const base = { ...previous, messages: without };
  return saved ? mergeMessage(base, saved) : base;
}

/** Live connection mode shown to the customer. */
export type LiveMode = 'live' | 'connecting' | 'polling';

/**
 * Socket up → live. Down for less than `fallbackSeconds` → still "connecting" (no banner yet).
 * Down longer → polling, with the reconnect banner.
 */
export function liveMode(
  state: { connected: boolean; downSince: number | null },
  now: number,
  fallbackSeconds: number,
): LiveMode {
  if (state.connected) return 'live';
  if (state.downSince === null) return 'connecting';
  return now - state.downSince >= fallbackSeconds * 1000 ? 'polling' : 'connecting';
}

/** Linear interpolation between two map points (t clamped to 0..1). */
export function lerpPoint(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  t: number,
): { lat: number; lng: number } {
  const k = Math.min(1, Math.max(0, t));
  return { lat: from.lat + (to.lat - from.lat) * k, lng: from.lng + (to.lng - from.lng) * k };
}

/**
 * What the ETA line says right now: a range before pickup ("25–32 min"), a countdown after
 * ("12 min"), late when the latest promised minute has passed, nothing once finished.
 */
export type EtaDisplay =
  | { kind: 'range'; from: number; to: number }
  | { kind: 'countdown'; minutes: number }
  | { kind: 'late' }
  | { kind: 'none' };

export function etaDisplay(track: OrderTrack, now: number): EtaDisplay {
  const eta = track.eta;
  if (eta.mode === 'none' || !eta.toAt) return { kind: 'none' };
  const minutesUntil = (iso: string) => Math.ceil((time(iso) - now) / 60_000);
  const to = minutesUntil(eta.toAt);
  if (to <= 0) return { kind: 'late' };
  if (eta.mode === 'countdown') return { kind: 'countdown', minutes: Math.max(1, to) };
  const from = eta.fromAt ? Math.max(1, minutesUntil(eta.fromAt)) : to;
  return { kind: 'range', from: Math.min(from, to), to };
}
